import React, { useState, useEffect } from 'react';
import useApi from '../api/axiosAdapter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { Button, Input, ModalFooter, ModalLabel, SearchContainer, StatusMessage } from '../../GlobalStyles';
import { Td, NarrowTd, SearchPopup, TableContainer, TableHeader } from '../Table/TableStyle';
import { useAuth } from '../Authorization/AuthContext';
import { PermissionCheckers } from '../Authorization/permissions';
import { PCARCommentInfo } from './DataReview.interfaces';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import AppTable from '../Table/AppTable';
import LetterTextModal from './LetterTextModal';
import CommentEditModal from './CommentEditModal';

// Relate the column display names to the keys in the CommentInfo interface
const dataColumns = [
    { displayName: 'Actions', key: 'actions', width: 1, isCentered: true, searchable: false, sortable: false },
    { displayName: 'Letter_comment ID', key: 'letter_comment_num', width: 1, isCentered: true },
    { displayName: 'Comment', key: 'comment_text', width: 50, isCentered: true },
    { displayName: 'Code', key: 'comment_code', width: 1, isCentered: true },
    { displayName: 'Level of Effort Required', key: 'swca_rank', width: 1, isCentered: true },
    { displayName: 'Response', key: 'response', width: 50, isCentered: true },
    { displayName: 'Document Edit', key: 'tech_edit', width: 50 },
    { displayName: 'Agency Comments', key: 'agency_comments', width: 50 },
    { displayName: 'PM Notes', key: 'pm_notes', width: 50, isCentered: true },
    { displayName: 'Doc Edit Complete', key: 'doc_edit_complete', width: 1, isCentered: true, searchable: false, sortable :false },
    { displayName: 'PM Approval', key: 'pm_complete', width: 1, isCentered: true, searchable: false, sortable: false },
];

interface CommentReviewTableProps {
    data: PCARCommentInfo[];
    onDataChange?: () => void;
}

const CommentReviewTable: React.FC<CommentReviewTableProps> = ({ data, onDataChange }) => {
    const [tableData, setTableData] = useState<PCARCommentInfo[]>([]);
    const [search, setSearch] = useState<string>('');
    const [searchColumn, setSearchColumn] = useState<keyof PCARCommentInfo | null>(null);
    const [sortConfig, setSortConfig] = useState<{ key: keyof PCARCommentInfo; direction: 'asc' | 'desc' } | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [statusType, setStatusType] = useState<string>('');
    const [isLetterTextModalOpen, setIsLetterTextModalOpen] = useState<boolean>(false);
    const [selectedLetterRecord, setSelectedLetterRecord] = useState<PCARCommentInfo | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [recordToEdit, setRecordToEdit] = useState<PCARCommentInfo | null>(null);

    const { post } = useApi();
    const { authorization, selectedProject } = useAuth();

    // Derive data review permission flags
    const isPM = PermissionCheckers.isDataReviewPM(authorization, selectedProject);
    const isSWCANonPM = PermissionCheckers.isDataReviewSWCANonPM(authorization, selectedProject);
    const isAgencyUser = PermissionCheckers.isDataReviewAgency(authorization);
    const isSWCAUser = isPM || isSWCANonPM;

    // Agency users cannot see swca_rank, pm_notes, doc_edit_complete, or pm_complete columns
    const visibleColumns = dataColumns.filter(col => {
        if (isAgencyUser) {
            return !['swca_rank', 'pm_notes', 'doc_edit_complete', 'pm_complete'].includes(col.key);
        }
        return true;
    });

    useEffect(() => {
        setTableData(data);
    }, [data]);

    // Handle search input change
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    // Handle sorting
    const handleSort = (key: keyof PCARCommentInfo, direction: 'asc' | 'desc') => {
        setSortConfig({ key, direction });
    };

    // Handle search submission
    const handleSearchSubmit = (key: keyof PCARCommentInfo) => {
        setSearchColumn(key);
    };

    // Helper function to update any response field
    const updateResponseField = async (
        commentUuid: string, 
        fieldName: string,
        value: string | number | boolean
    ) => {
        try {
            const url = `pcar/update_response`;
            const payload = {
                comment_uuid: commentUuid,
                updates: {
                    [fieldName]: value
                }
            };
            await post(url, payload);
            return true;
        } catch (error) {
            console.error(`Error updating ${fieldName}:`, error);
            setStatusMessage(`Error updating ${fieldName}: ` + error);
            setStatusType('error');
            return false;
        }
    };

    // Handle PM Complete checkbox change
    const handlePMCompleteChange = async (record: PCARCommentInfo) => {
        const newValue = !record.pm_complete;
        const success = await updateResponseField(record.comment_uuid, 'pm_complete', newValue);
        
        if (success) {
            const updatedData = tableData.map((item) => {
                if (item.comment_uuid === record.comment_uuid) {
                    return {
                        ...item,
                        pm_complete: newValue
                    };
                }
                return item;
            });
            setTableData(updatedData);
            setStatusMessage(`PM Complete updated successfully for comment ${record.letter_comment_num}`);
            setStatusType('success');
        }
    };

    // Handle Doc Edit Complete checkbox change
    const handleDocEditCompleteChange = async (record: PCARCommentInfo) => {
        const newValue = !record.doc_edit_complete;
        const success = await updateResponseField(record.comment_uuid, 'doc_edit_complete', newValue);
        
        if (success) {
            const updatedData = tableData.map((item) => {
                if (item.comment_uuid === record.comment_uuid) {
                    return {
                        ...item,
                        doc_edit_complete: newValue
                    };
                }
                return item;
            });
            setTableData(updatedData);
            setStatusMessage(`Doc Edit Complete updated successfully for comment ${record.letter_comment_num}`);
            setStatusType('success');
        }
    };

    // Handle letter comment number click to open letter text modal
    const handleLetterCommentClick = (record: PCARCommentInfo) => {
        setSelectedLetterRecord(record);
        setIsLetterTextModalOpen(true);
    };

    const handleCloseLetterTextModal = () => {
        setIsLetterTextModalOpen(false);
        setSelectedLetterRecord(null);
    };

    // Handle SWCA Rank change
    const handleSwcaRankChange = async (record: PCARCommentInfo, newValue: string) => {
        const newRank = parseInt(newValue, 10);
        
        // Validate the input
        if (isNaN(newRank) || newRank < 0) {
            setStatusMessage('Level of Effort must be a positive number');
            setStatusType('error');
            return;
        }

        const success = await updateResponseField(record.comment_uuid, 'swca_rank', newRank);
        
        if (success) {
            const updatedData = tableData.map((item) => {
                if (item.comment_uuid === record.comment_uuid) {
                    return {
                        ...item,
                        swca_rank: newRank
                    };
                }
                return item;
            });
            setTableData(updatedData);
            setStatusMessage(`Level of Effort updated successfully for comment ${record.letter_comment_num}`);
            setStatusType('success');
        }
    };

    // Handle edit button click
    const handleEditClick = (record: PCARCommentInfo) => {
        setRecordToEdit(record);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setRecordToEdit(null);
    };

    const handleSaveEdit = (updatedRecord: PCARCommentInfo) => {
        // Refresh data from API to get latest changes including agency comments
        if (onDataChange) {
            onDataChange();
        }
        setStatusMessage(`Comment #${updatedRecord.letter_comment_num} updated successfully`);
        setStatusType('success');
    };


    // Sort and filter data
    const sortedData = React.useMemo(() => {
        let sortableData = [...tableData];

        // Apply search filter
        if (search && searchColumn) {
           sortableData = sortableData.filter((item) =>
                    String(item[searchColumn]).toLowerCase().includes(search.toLowerCase())
                );
        }

        // Apply sorting
        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [tableData, search, searchColumn, sortConfig]);

    // Function to format date as YYYY-MM-DD
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to safely render field values
    const renderFieldValue = (key: string, value: any): React.ReactNode => {
        if (key === 'actions') {
            // This is handled separately in the AppTable rendering
            return null;
        }
        if (key === 'created_on' && typeof value === 'string') {
            return formatDate(value);
        }
        if (key === 'agency_comments') {
            if (Array.isArray(value) && value.length > 0) {
                return (
                    <ul style={{ 
                        textAlign: 'left', 
                        margin: 0, 
                        paddingLeft: '20px',
                        listStyleType: 'disc'
                    }}>
                        {value.map((ac, index) => (
                            <li key={`${ac.comment_uuid}-${index}`} style={{ marginBottom: '4px' }}>
                                {ac.comment_text} ({ac.name})
                            </li>
                        ))}
                    </ul>
                );
            }
            return value || '';
        }
        // For all other fields, convert to string if needed
        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value);
        }
        return value;
    };

    return (
        <TableContainer>
            <TableHeader>
                <StatusMessage statusType={statusType} >
                    {statusMessage}&nbsp;
                </StatusMessage>
                {searchColumn && (
                    <SearchPopup>
                        {(() => {
                            const col_label = dataColumns.find((col) => col.key === searchColumn)?.displayName;
                            return (
                                <SearchContainer style={{ width: '200px', padding: '10px', border: '1px solid #ccc', backgroundColor: '#ccc', }}>
                                    <ModalLabel>Search {col_label} :</ModalLabel>
                                    <Input type="text" value={search} onChange={handleSearch} placeholder={`Search ${col_label}`} />
                                    <ModalFooter>
                                        <Button variant='secondary' onClick={() => { setSearchColumn(null); setSearch(''); }}>Cancel</Button>
                                    </ModalFooter>
                                </SearchContainer>
                            );
                        })()}
                    </SearchPopup>
                )}
            </TableHeader>
            <AppTable
                columns={visibleColumns}
                onSort={(key, dir) => handleSort(key as keyof PCARCommentInfo, dir)}
                onSearch={(key) => handleSearchSubmit(key as keyof PCARCommentInfo)}
                showSortIcons={true}
                sortConfig={sortConfig}
                searchColumn={searchColumn}
            >
                {sortedData.map((record) => (
                    <tr key={record.comment_uuid}>
                        {visibleColumns.map((col) => {
                            if (col.key === 'actions') {
                                return (
                                    <NarrowTd key="actions">
                                        <FontAwesomeIcon
                                            icon={record.pm_complete ? faEye : faPencilAlt}
                                            onClick={() => handleEditClick(record)}
                                            style={{ 
                                                cursor: 'pointer', 
                                                color: '#007bff',
                                                fontSize: '16px'
                                            }}
                                            title={record.pm_complete ? 'View' : 'Edit'}
                                        />
                                    </NarrowTd>
                                );
                            }
                            if (col.key === 'letter_comment_num') {
                                return (
                                    <NarrowTd key="letter_comment_num">
                                        <span 
                                            onClick={() => handleLetterCommentClick(record)}
                                            style={{ 
                                                color: '#007bff', 
                                                cursor: 'pointer', 
                                                textDecoration: 'underline',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {record.letter_comment_num}
                                        </span>
                                    </NarrowTd>
                                );
                            }
                            if (col.key === 'swca_rank') {
                                // SWCA users can toggle level of effort (disabled if PM approval locked)
                                const rankLocked = record.pm_complete;
                                return (
                                    <NarrowTd key="swca_rank">
                                        {isSWCAUser ? (
                                            <input
                                                type="number"
                                                min="0"
                                                value={record.swca_rank || 0}
                                                onChange={(e) => handleSwcaRankChange(record, e.target.value)}
                                                disabled={rankLocked}
                                                style={{ 
                                                    width: '80px', 
                                                    height: '32px',
                                                    padding: '6px 8px', 
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    opacity: rankLocked ? 0.5 : 1,
                                                }}
                                            />
                                        ) : (
                                            <span>{record.swca_rank || 0}</span>
                                        )}
                                    </NarrowTd>
                                );
                            }
                            if (col.key === 'pm_complete') {
                                // PM/sysAdmin gets checkbox; SWCA non-PM sees icon only
                                return (
                                    <NarrowTd key="pm_complete">
                                        {isPM ? (
                                            <input
                                                type="checkbox"
                                                checked={record.pm_complete}
                                                onChange={() => handlePMCompleteChange(record)}
                                                style={{ width: '20px', height: '20px' }}
                                            />
                                        ) : record.pm_complete ? (
                                            <FontAwesomeIcon icon={faCheck} style={{ color: '#28a745', fontSize: '16px' }} />
                                        ) : null}
                                    </NarrowTd>
                                );
                            }
                            if (col.key === 'doc_edit_complete') {
                                // PM/sysAdmin gets checkbox (disabled when PM approval is locked); SWCA non-PM sees icon only
                                const docLocked = record.pm_complete;
                                return (
                                    <NarrowTd key="doc_edit_complete">
                                        {isPM ? (
                                            <input
                                                type="checkbox"
                                                checked={record.doc_edit_complete}
                                                onChange={() => handleDocEditCompleteChange(record)}
                                                disabled={docLocked}
                                                style={{ width: '20px', height: '20px', opacity: docLocked ? 0.5 : 1 }}
                                            />
                                        ) : record.doc_edit_complete ? (
                                            <FontAwesomeIcon icon={faCheck} style={{ color: '#28a745', fontSize: '16px' }} />
                                        ) : null}
                                    </NarrowTd>
                                );
                            }
                            if (col.key === 'comment_num') {
                                return (
                                    <NarrowTd key="comment_num">
                                        {renderFieldValue(col.key, record[col.key as keyof PCARCommentInfo])}
                                    </NarrowTd>
                                );
                            }
                            return (
                                <Td key={col.key}>
                                    {renderFieldValue(col.key, record[col.key as keyof PCARCommentInfo])}
                                </Td>
                            );
                        })}
                    </tr>
                ))}
            </AppTable>
            {selectedLetterRecord && (
                <LetterTextModal
                    isOpen={isLetterTextModalOpen}
                    onClose={handleCloseLetterTextModal}
                    letterText={selectedLetterRecord.letter_text || 'No letter text available'}
                    commentText={selectedLetterRecord.comment_text}
                    letterCommentNum={selectedLetterRecord.letter_comment_num}
                />
            )}
            {recordToEdit && (
                <CommentEditModal
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    record={recordToEdit}
                    currentUserId={authorization?.userId || 0}
                    isPM={isPM}
                    isSWCANonPM={isSWCANonPM}
                    isAgencyUser={isAgencyUser}
                    onSave={handleSaveEdit}
                    onDataChange={onDataChange}
                />
            )}
        </TableContainer>
    );
}

export default CommentReviewTable;